// om-file.ts — SELF-CONTAINED clean-room Open-Meteo `.om` reader (TS port).
//
// Faithful translation of poc/om-file.mjs + poc/om-reader.mjs. Given only the
// raw file bytes (browser fetch ArrayBuffer/Uint8Array) + a variable name (or
// root), this produces the full decoded array using ONLY the file: metadata
// walk -> compressed-LUT decode -> chunk decode. No fixture, no GPL source.
//
// Clean-room: container/metadata model derived from binary inspection + the
// black-box oracle's getBytes access pattern (NOT its source). The PForDelta
// codec lives in p4codec.ts (Apache-2.0 goturbopfor adaptation). See
// DECODER-NOTES.md "SESSION 6 — LUT decode" for the LUT encoding.
//
// TS PORT NOTE: Node Buffer methods (readBigUInt64LE / readUInt32LE /
// readFloatLE / indexOf) are replaced with a DataView over the same bytes and
// a small Uint8Array indexOf. All offsets / arithmetic are unchanged.

import { p4nddec64 } from './p4codec.js'
import { fullDecode as chunkFullDecode, type ChunkMeta } from './om-decode.js'

// ---------------------------------------------------------------------------
// Byte-reading helpers (replace Node Buffer accessors; little-endian).
// ---------------------------------------------------------------------------

function normalize(input: Uint8Array | ArrayBuffer): Uint8Array {
  return input instanceof Uint8Array ? input : new Uint8Array(input)
}

function makeView(buf: Uint8Array): DataView {
  return new DataView(buf.buffer, buf.byteOffset, buf.byteLength)
}

// Read a little-endian u64 as a JS number (values here are file offsets/sizes
// well within Number.MAX_SAFE_INTEGER, matching the .mjs `Number(readBigUInt64LE)`).
function readU64(view: DataView, off: number): number {
  return Number(view.getBigUint64(off, true))
}

function readU32(view: DataView, off: number): number {
  return view.getUint32(off, true)
}

function readF32(view: DataView, off: number): number {
  return view.getFloat32(off, true)
}

// indexOf for a Uint8Array needle within a Uint8Array haystack, from `start`.
// Mirrors Buffer.prototype.indexOf semantics for the byte-search used here.
function indexOfBytes(hay: Uint8Array, needle: Uint8Array, start: number): number {
  const n = needle.length
  if (n === 0) return start
  const last = hay.length - n
  for (let i = start; i <= last; i++) {
    let k = 0
    while (k < n && hay[i + k] === needle[k]) k++
    if (k === n) return i
  }
  return -1
}

function asciiBytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff
  return out
}

// ---------------------------------------------------------------------------
// Trailer + array-node parse (port of om-reader.mjs).
// ---------------------------------------------------------------------------

export interface OmTrailer {
  version: number
  rootOffset: number
  rootSize: number
}

// Trailer = last 24 bytes: ['O','M', version u8, 5 pad, rootOffset u64 LE, rootSize u64 LE].
export function parseTrailer(buf: Uint8Array, view: DataView): OmTrailer {
  const base = buf.length - 24
  if (buf[base] !== 0x4f || buf[base + 1] !== 0x4d) throw new Error('bad OM trailer magic')
  return {
    version: buf[base + 2],
    rootOffset: readU64(view, base + 8),
    rootSize: readU64(view, base + 16),
  }
}

export interface OmArrayNode {
  nodeOff: number
  childCount: number
  lutSize: number
  lutOffset: number
  scaleFactor: number
  tag: number
}

// Array-node layout (VALIDATED against oracle):
//   +0  u64 packed (lo32 per-var word, hi32 childCount)
//   +8  u64 lutSize, +16 u64 lutOffset, +32 f32 scaleFactor, +40 u64 tag
export function readArrayNode(view: DataView, nodeOff: number): OmArrayNode {
  return {
    nodeOff,
    childCount: readU32(view, nodeOff + 4),
    lutSize: readU64(view, nodeOff + 8),
    lutOffset: readU64(view, nodeOff + 16),
    scaleFactor: readF32(view, nodeOff + 32),
    tag: readU64(view, nodeOff + 40),
  }
}

// ---------------------------------------------------------------------------
// LUT (chunk-offset lookup table) decode.
//
// The LUT stores nChunks+1 ABSOLUTE chunk-start byte offsets, split into blocks
// of LUT_BLOCK = 64 offsets. Each block is positioned at a FIXED even stride
// `lutChunkLength = lutSize / nLutChunks` and is compressed as a single
// p4ndenc64 unit: a leading varbyte start value + a 64-bit TurboPFor DELTA
// block. (om-file-format: c/src/om_decoder.c + c/src/om_encoder.c, constant
// LUT_CHUNK_COUNT = 64.) Decoded here with p4nddec64 (bit-exact vs the
// @openmeteo oracle for HRRR / MET Nordic / UKMO / ICON Global).
// ---------------------------------------------------------------------------
export const LUT_BLOCK = 64 // LUT_CHUNK_COUNT — offsets per LUT block (OM format constant)

// Decode `nOffsets` absolute chunk-start offsets from the compressed LUT.
export function decodeLut(
  buf: Uint8Array,
  lutOffset: number,
  lutSize: number,
  nOffsets: number,
): number[] {
  const nBlocks = Math.ceil(nOffsets / LUT_BLOCK)
  const lutChunkLength = Math.floor(lutSize / nBlocks)
  const out = new Array<number>(nOffsets)
  for (let blk = 0; blk < nBlocks; blk++) {
    const p = lutOffset + blk * lutChunkLength
    const off = blk * LUT_BLOCK
    const cnt = Math.min(LUT_BLOCK, nOffsets - off)
    p4nddec64(buf, p, out, off, cnt)
  }
  return out
}

// ---------------------------------------------------------------------------
// Metadata extraction (self-contained, no fixture).
// ---------------------------------------------------------------------------

// Read nDims dims[] + chunk[] that immediately precede a null-terminated name.
// Layout right before the name: ...[dim0..dim{n-1}][chunk0..chunk{n-1}]["name\0"].
function readDimsChunk(
  view: DataView,
  nameOff: number,
  nDims: number,
): { dims: number[]; chunk: number[]; dimsStart: number } {
  const chunkStart = nameOff - nDims * 8
  const dimsStart = chunkStart - nDims * 8
  const dims: number[] = []
  const chunk: number[] = []
  for (let i = 0; i < nDims; i++) {
    dims.push(readU64(view, dimsStart + i * 8))
    chunk.push(readU64(view, chunkStart + i * 8))
  }
  return { dims, chunk, dimsStart }
}

const chunkGrid = (dims: number[], chunk: number[]): number[] =>
  dims.map((d, i) => Math.ceil(d / chunk[i]))
const numChunks = (dims: number[], chunk: number[]): number =>
  chunkGrid(dims, chunk).reduce((a, b) => a * b, 1)

// Validate a candidate (dims, chunk, node): array-node fields sane AND the LUT
// decodes to a monotonic, in-bounds offset array of the expected length.
function tryDecodeMeta(
  buf: Uint8Array,
  node: OmArrayNode,
  dims: number[],
  chunk: number[],
): number[] | null {
  if (!(node.lutOffset > 3 && node.lutOffset < buf.length)) return null
  if (!(node.lutSize > 0 && node.lutSize < buf.length)) return null
  if (
    !Number.isFinite(node.scaleFactor) ||
    node.scaleFactor === 0 ||
    Math.abs(node.scaleFactor) > 1e12
  )
    return null
  if (dims.some(d => !(d > 0 && d < 1e8))) return null
  if (chunk.some((c, i) => !(c > 0 && c <= dims[i]))) return null
  const nC = numChunks(dims, chunk)
  if (!(nC > 0 && nC < 5e7)) return null
  let starts: number[]
  try {
    starts = decodeLut(buf, node.lutOffset, node.lutSize, nC + 1)
  } catch {
    return null
  }
  // self-checks: monotonic non-decreasing, all within [3, lutOffset].
  if (!(starts[0] >= 3 && starts[0] <= node.lutOffset)) return null
  for (let i = 1; i < nC + 1; i++) {
    if (!(starts[i] >= starts[i - 1])) return null
    if (!(starts[i] <= node.lutOffset)) return null
  }
  return starts
}

export interface OmVariable {
  dims: number[]
  chunk: number[]
  grid: number[]
  scale: number
  lutOffset: number
  lutSize: number
  starts: number[] // length numChunks+1; chunk ci spans [starts[ci], starts[ci+1])
  // decode() returns the full flat Int32Array of raw (pre-scale) values.
  decode(): Int32Array
}

// Locate the array node for `varName` (or the root array when varName is null),
// extract { dims, chunk, scale, lutOffset, lutSize, starts } from the file ONLY.
export function readVariable(input: Uint8Array | ArrayBuffer, varName: string | null): OmVariable {
  const buf = normalize(input)
  const view = makeView(buf)
  const tr = parseTrailer(buf, view)

  if (!varName) {
    // root-as-array: the array node IS at rootOffset. The (empty) name sits at
    // rootOffset + rootSize. Detect nDims by self-consistency of the LUT.
    const node = readArrayNode(view, tr.rootOffset)
    const nameOffRoot = tr.rootOffset + tr.rootSize
    for (let nDims = 1; nDims <= 6; nDims++) {
      const dc = readDimsChunk(view, nameOffRoot, nDims)
      const starts = tryDecodeMeta(buf, node, dc.dims, dc.chunk)
      if (starts) return finish(buf, node, dc.dims, dc.chunk, starts)
    }
    throw new Error('could not resolve root-array dims/chunk from file')
  }

  // named child (spatial): the array node stores dims[]/chunk[] immediately
  // before its name string. Scan for every occurrence of the exact name that is
  // NOT immediately followed by another name character; for each, try the
  // backward dims/chunk walk and accept the first array-node + LUT self-check.
  const needle = asciiBytes(varName)
  const isNameByte = (b: number): boolean =>
    (b >= 48 && b <= 57) || (b >= 65 && b <= 90) || (b >= 97 && b <= 122) || b === 95
  let scan = 0
  while ((scan = indexOfBytes(buf, needle, scan)) >= 0) {
    const after = buf[scan + needle.length]
    if (!isNameByte(after)) {
      for (let nDims = 1; nDims <= 6; nDims++) {
        const dc = readDimsChunk(view, scan, nDims)
        const n = readArrayNode(view, dc.dimsStart - 56)
        const starts = tryDecodeMeta(buf, n, dc.dims, dc.chunk)
        if (starts) return finish(buf, n, dc.dims, dc.chunk, starts)
      }
    }
    scan += 1
  }
  throw new Error('could not resolve dims/chunk for variable: ' + varName)
}

function finish(
  buf: Uint8Array,
  node: OmArrayNode,
  dims: number[],
  chunk: number[],
  starts: number[],
): OmVariable {
  const grid = chunkGrid(dims, chunk)
  return {
    dims,
    chunk,
    grid,
    scale: node.scaleFactor,
    lutOffset: node.lutOffset,
    lutSize: node.lutSize,
    starts,
    decode(): Int32Array {
      const meta: ChunkMeta = { dims, chunk, grid, starts }
      return chunkFullDecode(meta, buf)
    },
  }
}

// Convenience: open a file buffer and return the self-contained variable handle.
export function openVariable(
  input: Uint8Array | ArrayBuffer,
  varName: string | null = null,
): OmVariable {
  return readVariable(input, varName)
}
